/**
 * useSocket — один коннект, не отключаем. Частота опроса:
 * - Авторизован: каждые 3 сек
 * - Не авторизован: каждые 15 сек
 */
import { useEffect } from 'react';
import { WS_RATE_URL, WS_BAL_URL, URL_API } from './config/endpoints';
import { getTgUserId, getTgInitData } from './tgUser';

// Разово сообщаем бэкенду о входе (он уведомит админов в TG). Только с подписанной
// initData — бэк валидирует её и берёт НАСТОЯЩИЙ id (фейковых id не шлём вовсе).
let _enterReported = false;
function reportEnter(initData) {
  if (_enterReported || !initData) return;
  _enterReported = true;
  try {
    fetch(`${URL_API}/enter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', initData },
      body: JSON.stringify({ initData }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {}
}

const POLL_AUTH_MS = 3000;   // авторизован: каждые 3 сек
const POLL_NO_AUTH_MS = 15000; // не авторизован: каждые 15 сек

function createChannel(url, needsAuth = false) {
  let ws = null;
  let pollTimer = null;
  const listeners = new Set();
  let lastData = null;
  let lastAt = 0;
  let initData = null;
  let userId = null; // фолбэк-авторизация по user_id (вне Telegram)
  let connecting = false;
  let reconnectAttempts = 0;
  let reconnectingForAuth = false;
  const MAX_RECONNECT = 5;

  function buildUrl() {
    if (needsAuth) {
      if (initData) return `${url}?initdata=${encodeURIComponent(initData)}`;
      if (userId) return `${url}?user_id=${encodeURIComponent(userId)}`;
    }
    return url;
  }

  function getPollMs() {
    if (needsAuth) return (initData || userId) ? POLL_AUTH_MS : POLL_NO_AUTH_MS;
    return POLL_AUTH_MS; // rate всегда часто
  }

  function schedulePoll() {
    if (pollTimer) clearInterval(pollTimer);
    const ms = getPollMs();
    pollTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        try { ws.send('get'); } catch {}
      }
    }, ms);
  }

  function connect() {
    if (connecting) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

    connecting = true;
    ws = new WebSocket(buildUrl());

    ws.onopen = () => {
      connecting = false;
      reconnectAttempts = 0;
      schedulePoll();
      try { ws.send('get'); } catch {}
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        lastData = data;
        lastAt = Date.now();
        listeners.forEach(fn => { try { fn(data); } catch {} });
      } catch {}
    };

    ws.onclose = () => {
      connecting = false;
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      if (reconnectingForAuth) {
        reconnectingForAuth = false;
        connect();
        return;
      }
      if (reconnectAttempts < MAX_RECONNECT) {
        reconnectAttempts++;
        setTimeout(connect, 2000);
      }
    };
  }

  function reconnectForAuth() {
    if (!needsAuth) return;
    if (!ws || ws.readyState >= WebSocket.CLOSING) {
      reconnectAttempts = 0;
      connect();
    } else {
      reconnectingForAuth = true;
      reconnectAttempts = 0;
      try { ws.close(); } catch {}
    }
    schedulePoll();
  }

  return {
    setInitData(data) {
      if (data && initData !== data) {
        initData = data;
        reconnectForAuth();
      }
    },
    // Авторизация по user_id (вне Telegram). Реальный initData имеет приоритет.
    setUserId(uid) {
      const v = uid == null ? null : String(uid);
      if (v && userId !== v) {
        userId = v;
        if (!initData) reconnectForAuth();
      }
    },
    connect() {
      connect();
    },
    addListener(fn) {
      listeners.add(fn);
      if (lastData && (Date.now() - lastAt) <= 3000) try { fn(lastData); } catch {}
      connect();
      return () => listeners.delete(fn);
    },
    send(msg) {
      if (ws?.readyState === WebSocket.OPEN) {
        try { ws.send(typeof msg === 'string' ? msg : JSON.stringify(msg)); } catch {}
      }
    },
  };
}

export const rateChannel = createChannel(WS_RATE_URL, false);
export const balChannel = createChannel(WS_BAL_URL, true);

rateChannel.connect();
balChannel.connect(); // подключаем сразу даже без initData — сервер держит коннект с uid=0

export function useInitData() {
  useEffect(() => {
    // Telegram-webview может прогреваться и отдавать initData/id с задержкой,
    // поэтому не «один раз на монтировании», а опрашиваем ~7с. Приоритет —
    // ПОДПИСАННАЯ initData (бэк валидирует HMAC → реальный id). Как только
    // она появилась, авторизуемся ею и прекращаем опрос.
    let stop = false;
    let tries = 0;
    const MAX_TRIES = 18; // ~7с при шаге 400мс

    const tick = () => {
      if (stop) return true;
      tries += 1;

      const initData = getTgInitData();
      if (initData) {
        balChannel.setInitData(initData); // подписанная — реальный id на бэке
        reportEnter(initData);            // разово уведомляем админов о входе
        return true;
      }

      // Пока подписи нет — авторизуемся по реальному user_id, если он уже доступен.
      // Фейковый '1' больше НЕ используем: только настоящий Telegram id.
      const uid = getTgUserId();
      if (uid) balChannel.setUserId(uid);

      return tries >= MAX_TRIES;
    };

    if (tick()) return;
    const iv = setInterval(() => { if (tick()) clearInterval(iv); }, 400);

    // Событие готовности вьюпорта Telegram — дёргаем резолв сразу, не дожидаясь тика
    let tg = null;
    try { tg = window?.Telegram?.WebApp; } catch (e) {}
    const onReady = () => { tick(); };
    try { tg?.onEvent?.('viewportChanged', onReady); } catch (e) {}

    return () => {
      stop = true;
      clearInterval(iv);
      try { tg?.offEvent?.('viewportChanged', onReady); } catch (e) {}
    };
  }, []);
}
